import React, { useState } from "react";
import { Dropdown, Button, Icon, Form, Label, Popup } from "semantic-ui-react";
import { get, map } from "lodash";
import Text from "@Components/Text";
import TextArea from "@Components/TextArea";
import styles from "./styles.module.css";
import FilterCard from "./Components";
import FilterSelectorField from "./Components/FilterSelectorField";
import DateRangePicker from "@Components/DateRangePicker";
import DropdownOrder from "./Components/DropdownOrder";
import PropTypes from "prop-types";
import { chatStatusOptions, stateOptions, actionOptions, sectionOptions, groupByOptions } from "./staticData";
import { formatPhone } from "@Utils/formats";

const renderContactDetails = (client) => {
	const parts = [];
	const channel = get(client, "contact.channel");
	const email = get(client, "email");
	const channelAccountId = get(client, "contact.channelAccountId");
	const phone = get(client, "phone");

	if (channel) parts.push(`Canal: ${channel}`);

	const contactHandle = email || channelAccountId;
	const cleanPhone = phone ? phone.toString().replace(/\D/g, "") : "";
	const cleanHandle = contactHandle ? contactHandle.toString().replace(/\D/g, "") : "";

	if (contactHandle && (!phone || cleanHandle !== cleanPhone)) {
		parts.push(contactHandle);
	}

	if (phone) parts.push(formatPhone(phone));
	return parts.join(" | ");
};

const renderUserHeader = (client, defaultName) => {
	let dotColor = "transparent";
	const state = get(client, "state");
	if (state) {
		const s = state.toLowerCase();
		if (s === "conectado" || s.includes("linea") || s.includes("línea") || s === "online") dotColor = "#4caf50";
		else dotColor = "transparent";
	}
	return (
		<div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
			{dotColor !== "transparent" && (
				<span title={state} style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: dotColor, flexShrink: 0 }} />
			)}
			<span>{defaultName}</span>
		</div>
	);
};

const renderUserDetails = (client) => {
	const elements = [];
	const role = get(client, "role");
	const email = get(client, "user.email");
	const phone = get(client, "user.phone");

	if (role) {
		elements.push(<span key="role">Rol: {role}</span>);
	}
	if (email) {
		elements.push(<span key="email">{email}</span>);
	}
	if (phone) {
		elements.push(<span key="phone">{formatPhone(phone)}</span>);
	}

	return (
		<div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "6px" }}>
			{map(elements, (el, i) => {
				return (
					<React.Fragment key={el.key}>
						{el}
						{i < elements.length - 1 && <span style={{ color: "#d1d5db" }}>|</span>}
					</React.Fragment>
				);
			})}
		</div>
	);
};

const renderGroupDetails = (client) => {
	const typeName = get(client, "type.name");
	if (typeName) {
		return <span>Tipo: {typeName}</span>;
	}
	return null;
};

const renderChannelDetails = (client) => {
	const parts = [];
	const platformName = get(client, "platform.name");
	const useTemplate = get(client, "platform.useTemplate");

	if (platformName) {
		parts.push(`Plataforma: ${platformName}`);
	}
	if (useTemplate !== undefined) {
		parts.push(`Usa plantillas: ${useTemplate ? "Sí" : "No"}`);
	}
	if (parts.length > 0) return <span>{parts.join(" | ")}</span>;
	return null;
};

const FilterMaster = ({
	// Errors
	errors = {},
	// Report selection
	options = [],
	selectedFilterTemp,
	onChangeSelection,
	// Dates
	startDate,
	endDate,
	handleInput,
	getMaxDate,
	// Ordering
	tempOrderBy,
	handleOrderByChange,
	getOrderByOptions,
	orderDirectionOptions,
	tempOrderDirection,
	setTempOrderDirection,
	// Contacts
	contacts,
	onContactsSelect,
	onContactsRemove,
	// Users/Agents
	agents,
	setAgents,
	agentsData,
	// Tags
	tagsSelected,
	handleInputDropdown,
	tagsData,
	// Note/Text
	note,
	setNote,
	// Channels
	channelFilters,
	channelfiltersData,
	// Groups
	groupsData,
	groupsfilters,
	onChangeDropdown,
	// Actions
	cleanModal,
	// cleaned,
	// Actions history
	actionsHistory = [],
	setActionsHistory = () => {},
	// Categories
	categoriesSelected = [],
	categoriesData = [],
	handleCategoriesChange = () => {},
	// Chat status
	stateValue = "",
	setStateValue = () => {},
	conditionValue = [],
	setConditionValue = () => {},
	// Grouping
	groupBy = "",
	setGroupBy = () => {},
	// Section
	section = "",
	setSection = () => {},
	// Render props
	showTable = false,
	children = null,
	onApplyFilters = null,
	onCancelFilters = null,
	onEditFilters = null,
	onRemoveActiveFilter = null,
}) => {
	const [isSidebarVisible, setIsSidebarVisible] = useState(true);
	const selectedGroups = (groupsfilters || []).map((id) => {
		const found = (groupsData || []).find((g) => {
			return g.value === id;
		});
		return { id, name: found ? found.text : id };
	});

	const handleSetGroups = (selectedItems) => {
		const ids = (selectedItems || []).map((item) => {
			return item.id;
		});
		onChangeDropdown(ids, "groupsfilters");
	};

	const selectedChannels = (channelFilters || []).map((id) => {
		const found = (channelfiltersData || []).find((c) => {
			return c.value === id;
		});
		return { id, name: found ? found.text : id };
	});

	const handleSetChannels = (selectedItems) => {
		const ids = (selectedItems || []).map((item) => {
			return item.id;
		});
		onChangeDropdown(ids, "channelFilters");
	};

	const selectedAgents = (agents || []).map((id) => {
		const found = (agentsData || []).find((a) => {
			return a.value === id;
		});
		return { id, name: found ? found.text : id };
	});

	const handleSetAgents = (selectedItems) => {
		const ids = (selectedItems || []).map((item) => {
			return item.id;
		});
		setAgents(ids);
	};

	const selectedTags = (tagsSelected || []).map((id) => {
		const found = (tagsData || []).find((t) => {
			return t.value === id;
		});
		return { id, name: found ? found.text : id };
	});

	const handleSetTags = (selectedItems) => {
		const ids = (selectedItems || []).map((item) => {
			return item.id;
		});
		handleInputDropdown(null, { name: "tagsSelected", value: ids });
	};

	const selectedCategories = (categoriesSelected || []).map((id) => {
		const found = (categoriesData || []).find((c) => {
			return c.value === id;
		});
		return { id, name: found ? found.text : id };
	});

	const handleSetCategories = (selectedItems) => {
		const ids = (selectedItems || []).map((item) => {
			return item.id;
		});
		handleCategoriesChange(ids);
	};

	const handleSetContact = (selectedItem) => {
		if (selectedItem && selectedItem.id) {
			onContactsSelect(selectedItem);
		} else {
			onContactsRemove();
		}
	};

	const handleRemoveStartDate = () => {
		const cb = () => {
			handleInput({ target: { name: "startDate", value: "" } });
		};
		if (onRemoveActiveFilter) onRemoveActiveFilter(cb);
		else cb();
	};

	const handleRemoveEndDate = () => {
		const cb = () => {
			handleInput({ target: { name: "endDate", value: "" } });
		};
		if (onRemoveActiveFilter) onRemoveActiveFilter(cb);
		else cb();
	};

	const handleRemoveChannelFilters = () => {
		const cb = () => {
			onChangeDropdown([], "channelFilters");
		};
		if (onRemoveActiveFilter) onRemoveActiveFilter(cb);
		else cb();
	};

	const handleRemoveGroupsFilters = (e) => {
		e.stopPropagation();
		const cb = () => {
			onChangeDropdown([], "groupsfilters");
		};
		if (onRemoveActiveFilter) onRemoveActiveFilter(cb);
		else cb();
	};

	const handleRemoveActionsHistory = () => {
		const cb = () => {
			setActionsHistory([]);
		};
		if (onRemoveActiveFilter) onRemoveActiveFilter(cb);
		else if (setActionsHistory) cb();
	};

	const handleRemoveContacts = () => {
		if (onRemoveActiveFilter && onContactsRemove) onRemoveActiveFilter(onContactsRemove);
		else if (onContactsRemove) onContactsRemove();
	};

	const handleRemoveAgents = () => {
		const cb = () => {
			setAgents([]);
		};
		if (onRemoveActiveFilter) onRemoveActiveFilter(cb);
		else if (setAgents) cb();
	};

	const handleRemoveTagsSelected = () => {
		const cb = () => {
			handleInputDropdown(null, { value: [] });
		};
		if (onRemoveActiveFilter) onRemoveActiveFilter(cb);
		else if (handleInputDropdown) cb();
	};

	const handleRemoveCategoriesSelected = () => {
		const cb = () => {
			handleCategoriesChange([]);
		};
		if (onRemoveActiveFilter) onRemoveActiveFilter(cb);
		else if (handleCategoriesChange) cb();
	};

	const handleRemoveStateValue = () => {
		const cb = () => {
			setStateValue("");
		};
		if (onRemoveActiveFilter) onRemoveActiveFilter(cb);
		else if (setStateValue) cb();
	};

	return (
		<div className={styles.filterMasterLayout}>
			{isSidebarVisible ? (
				<div className={styles.sidebar}>
					<div className={styles.sidebarHeaderToggle} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
						<div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
							{onCancelFilters && !showTable ? (
								<button type="button" onClick={onCancelFilters} className={styles.backButton} title="Volver">
									<Icon name="arrow left" className={styles.backIcon} />
									<span className={styles.backText}>Volver</span>
								</button>
							) : (
								<span className={styles.filtersTitle}>Filtros</span>
							)}
						</div>
						<Icon
							name="angle right"
							className={styles.toggleIconBtn}
							onClick={() => {
								setIsSidebarVisible(false);
							}}
							title="Ocultar panel"
						/>
					</div>
					<div className={styles.sidebarActions}>
						{!showTable
							? onApplyFilters && (
									<Button onClick={onApplyFilters} className={styles.applyButton}>
										<Icon name="search" /> Aplicar
									</Button>
							  )
							: onEditFilters && (
									<Button onClick={onEditFilters} className={styles.applyButton}>
										<Icon name="filter" /> Editar
									</Button>
							  )}
						<Button onClick={cleanModal} basic color="red" className={styles.sidebarButton}>
							<Icon name="trash alternate outline" /> Limpiar
						</Button>
					</div>
					<div className={styles.sidebarSection}>
						<span className={styles.sidebarTitle}>Filtros Activos</span>
						<div className={styles.activeFilters}>
							{startDate && (
								<Label className={styles.filterPill}>
									Desde: {startDate} <Icon name="close" onClick={handleRemoveStartDate} />
								</Label>
							)}
							{endDate && (
								<Label className={styles.filterPill}>
									Hasta: {endDate} <Icon name="close" onClick={handleRemoveEndDate} />
								</Label>
							)}
							{selectedFilterTemp && (
								<Label className={styles.filterPill}>
									Tipo:{" "}
									{options.find((o) => {
										return o.value === selectedFilterTemp;
									})?.text || selectedFilterTemp}
								</Label>
							)}
							{channelFilters && channelFilters.length > 0 && (
								<Label className={styles.filterPill}>
									Canales: {channelFilters.length} <Icon name="close" onClick={handleRemoveChannelFilters} />
								</Label>
							)}
							{groupsfilters && groupsfilters.length > 0 && (
								<Popup
									trigger={
										<Label className={styles.filterPill} style={{ cursor: "pointer" }}>
											Grupos: {groupsfilters.length} <Icon name="close" onClick={handleRemoveGroupsFilters} />
										</Label>
									}
									position="left center"
									on="hover"
									className={styles.groupTooltip}
									basic>
									<Popup.Content>
										<ul className={styles.groupTooltipList}>
											{selectedGroups.map((group) => {
												return (
													<li key={group.id} className={styles.groupTooltipItem}>
														{group.name}
													</li>
												);
											})}
										</ul>
									</Popup.Content>
								</Popup>
							)}
							{actionsHistory && actionsHistory.length > 0 && (
								<Label className={styles.filterPill}>
									Acciones: {actionsHistory.length}
									<Icon name="close" onClick={handleRemoveActionsHistory} />
								</Label>
							)}
							{contacts && contacts.id && (
								<Label className={styles.filterPill}>
									Contacto: {contacts.name || contacts.id}
									<Icon name="close" onClick={handleRemoveContacts} />
								</Label>
							)}
							{agents && agents.length > 0 && (
								<Label className={styles.filterPill}>
									Usuarios: {agents.length}
									<Icon name="close" onClick={handleRemoveAgents} />
								</Label>
							)}
							{tagsSelected && tagsSelected.length > 0 && (
								<Label className={styles.filterPill}>
									Etiquetas: {tagsSelected.length}
									<Icon name="close" onClick={handleRemoveTagsSelected} />
								</Label>
							)}
							{categoriesSelected && categoriesSelected.length > 0 && (
								<Label className={styles.filterPill}>
									Categorías: {categoriesSelected.length}
									<Icon name="close" onClick={handleRemoveCategoriesSelected} />
								</Label>
							)}
							{stateValue && (
								<Label className={styles.filterPill}>
									Estado: {stateValue}
									<Icon name="close" onClick={handleRemoveStateValue} />
								</Label>
							)}
							{conditionValue && conditionValue.length > 0 && (
								<Label className={styles.filterPill}>
									Modo:{" "}
									{conditionValue
										.map((val) => {
											return (
												chatStatusOptions.find((o) => {
													return o.value === val;
												})?.text || val
											);
										})
										.join(", ")}
								</Label>
							)}
							{!startDate &&
								!endDate &&
								!selectedFilterTemp &&
								(!channelFilters || channelFilters.length === 0) &&
								(!groupsfilters || groupsfilters.length === 0) && (
									<div className={styles.emptyFiltersBox}>
										<Icon name="info circle" color="blue" />
										<span>Selecciona los filtros que deseas aplicar a tu reporte.</span>
									</div>
								)}
						</div>
					</div>
				</div>
			) : (
				<div
					className={styles.collapsedSidebar}
					role="button"
					tabIndex={0}
					onClick={() => {
						setIsSidebarVisible(true);
					}}
					onKeyDown={(e) => {
						if (e.key === "Enter" || e.key === " ") {
							setIsSidebarVisible(true);
						}
					}}
					title="Mostrar panel">
					<Icon name="angle left" className={styles.toggleIconBtn} />
				</div>
			)}

			<div className={styles.rightContent}>
				{showTable ? (
					children
				) : (
					<div className={styles.grid}>
						<div className={styles.containerFirst}>
							<FilterCard
								title="Filtrado Principal"
								icon="filter"
								leftAccent
								headerRight={
									errors &&
									errors.selected && (
										<Label basic color="red" pointing="left" style={{ margin: 0, padding: "0.5em 1em" }}>
											{errors.selected}
										</Label>
									)
								}>
								{" "}
								<div className={styles.dateContent}>
									<div className={styles.dateFieldDate}>
										<div className={styles.filterGrid3Col}>
											<div className={styles.userDropdownBlock}>
												<Text className={styles.lookUpLabel}>
													Reporte: <span className={styles.requiredAsterisk}>*</span>
												</Text>
												<Dropdown
													className={styles.filterDropdown}
													placeholder="Selecciona un Reporte"
													selection
													clearable
													options={sectionOptions}
													value={section}
													onChange={(e, { value }) => {
														return setSection(value);
													}}
												/>
											</div>
											<div className={styles.userDropdownBlock}>
												<Text className={styles.lookUpLabel}>
													Tipo: <span className={styles.requiredAsterisk}>*</span>
												</Text>
												<Dropdown
													className={styles.filterDropdown}
													placeholder="Selecciona el Tipo"
													selection
													search
													clearable
													disabled={!section}
													options={options}
													value={selectedFilterTemp}
													onChange={onChangeSelection}
												/>
											</div>
											{section && section !== "Generales" ? (
												<div className={styles.userDropdownBlock}>
													<Text className={styles.lookUpLabel}>
														Modo: <span className={styles.requiredAsterisk}>*</span>
													</Text>
													<Dropdown
														className={styles.filterDropdown}
														placeholder="Selecciona un modo"
														selection
														multiple
														clearable
														options={chatStatusOptions}
														value={conditionValue}
														onChange={(e, { value }) => {
															return setConditionValue(value);
														}}
													/>
												</div>
											) : (
												<div />
											)}
										</div>
									</div>
								</div>
								{section && selectedFilterTemp && (
									<div className={styles.dateContent} style={{ marginTop: "1rem", borderTop: "1px solid #e2e8f0", paddingTop: "1rem" }}>
										<div className={styles.dateFieldDate}>
											<DateRangePicker
												startDate={startDate}
												endDate={endDate}
												handleInput={handleInput}
												getMaxDate={getMaxDate}
												isMonthMode={selectedFilterTemp === "Año/Mes"}
											/>
											{section !== "Generales" && (
												<div className={styles.filterGrid3Col}>
													<div className={styles.userDropdownBlock}>
														<Text className={styles.lookUpLabel}>Estado:</Text>
														<Dropdown
															className={styles.filterDropdown}
															placeholder="Selecciona un estado"
															selection
															clearable
															options={stateOptions}
															value={stateValue}
															onChange={(e, { value }) => {
																return setStateValue(value);
															}}
														/>
													</div>
													{section &&
														["Detalladas", "No Trabajadas", "Etiquetas", "Etiquetas Grupo", "Año/Mes", "Acciones"].includes(
															selectedFilterTemp
														) && (
															<div className={styles.userDropdownBlock}>
																<DropdownOrder
																	orderByOptions={getOrderByOptions()}
																	orderByValue={tempOrderBy}
																	onOrderByChange={handleOrderByChange}
																	orderDirectionOptions={orderDirectionOptions}
																	orderDirectionValue={tempOrderDirection}
																	onOrderDirectionChange={setTempOrderDirection}
																	error={!!errors.selected && tempOrderBy && !tempOrderDirection}
																/>
															</div>
														)}
													{selectedFilterTemp !== "Detalladas" && (
														<div className={styles.userDropdownBlock}>
															<Text className={styles.lookUpLabel}>Agrupar por:</Text>
															<Dropdown
																className={styles.filterDropdown}
																placeholder="Selecciona agrupación"
																selection
																clearable
																options={groupByOptions}
																value={groupBy}
																onChange={(e, { value }) => {
																	return setGroupBy(value);
																}}
															/>
														</div>
													)}
												</div>
											)}
										</div>
									</div>
								)}
								{section && selectedFilterTemp === "Acciones" && (
									<div className={styles.containerOptions}>
										<div className={styles.filterGrid3Col}>
											<div className={styles.userDropdownBlock}>
												<Text className={styles.lookUpLabel}>Acciones:</Text>
												<Dropdown
													fluid
													multiple
													selection
													search
													clearable
													placeholder="Seleccionar Acciones"
													options={actionOptions}
													value={actionsHistory}
													onChange={(e, { value }) => {
														return setActionsHistory(value);
													}}
												/>
											</div>
										</div>
									</div>
								)}
							</FilterCard>
							<FilterCard leftAccent title="Filtros Adicionales" icon="sliders horizontal" collapsible defaultCollapsed={false}>
								<div className={styles.filterGrid3Col}>
									<FilterSelectorField
										label="Contacto"
										client={contacts || {}}
										setClient={handleSetContact}
										multiple={false}
										title="Seleccionar Contacto"
										searchClients="contacts"
										fields={["name", "phone", "email", "contact.channel", "contact.channelAccountId"]}
										orderKey="name"
										extraParams={{ isBlocked: 0 }}
										renderDetails={renderContactDetails}
										width="40%"
									/>
									<FilterSelectorField
										label="Usuarios"
										client={selectedAgents}
										setClient={handleSetAgents}
										multiple
										title="Seleccionar Usuarios"
										searchClients="users/all"
										idKey="userId"
										nameKey="user.fullName"
										fields={["user.fullName", "role", "state", "user.email", "user.phone"]}
										orderKey="user.fullName"
										renderName={renderUserHeader}
										renderDetails={renderUserDetails}
										width="40%"
									/>
									<FilterSelectorField
										label="Categorías"
										client={selectedCategories}
										setClient={handleSetCategories}
										multiple
										title="Seleccionar Categorías"
										searchClients="category/all"
										idKey="id"
										nameKey="name"
										fields={["name"]}
										orderKey="name"
										visible={
											selectedFilterTemp !== "Acciones" &&
											selectedFilterTemp !== "Resumidas" &&
											selectedFilterTemp !== "Hora/Usuarios" &&
											selectedFilterTemp !== "Hora/Grupos" &&
											selectedFilterTemp !== "Por Días" &&
											selectedFilterTemp !== "Por Horas"
										}
										width="40%"
									/>
									<FilterSelectorField
										label="Grupos"
										client={selectedGroups}
										setClient={handleSetGroups}
										multiple
										title="Seleccionar Grupos"
										visible={selectedFilterTemp !== "Acciones"}
										searchClients="groups/all"
										fields={["name", "type.name"]}
										orderKey="name"
										renderDetails={renderGroupDetails}
										width="40%"
									/>
									<FilterSelectorField
										label="Canales"
										client={selectedChannels}
										setClient={handleSetChannels}
										multiple
										title="Seleccionar Canales"
										searchClients="companyChannels"
										idKey="id"
										nameKey="label"
										fields={["label", "platform.name", "platform.useTemplate"]}
										orderKey="label"
										renderDetails={renderChannelDetails}
										width="40%"
									/>
									<FilterSelectorField
										label="Etiquetas"
										client={selectedTags}
										setClient={handleSetTags}
										multiple
										title="Seleccionar Etiquetas"
										searchClients="tags/all"
										idKey="id"
										nameKey="name"
										fields={["name"]}
										orderKey="name"
										visible={
											selectedFilterTemp !== "Acciones" &&
											selectedFilterTemp !== "Resumidas" &&
											selectedFilterTemp !== "Hora/Usuarios" &&
											selectedFilterTemp !== "Hora/Grupos" &&
											selectedFilterTemp !== "Por Días" &&
											selectedFilterTemp !== "Por Horas"
										}
										width="40%"
									/>
								</div>

								{selectedFilterTemp !== "Hora/Usuarios" &&
									selectedFilterTemp !== "Etiquetas" &&
									selectedFilterTemp !== "Etiquetas Grupo" &&
									selectedFilterTemp !== "Acciones" &&
									selectedFilterTemp !== "No Trabajadas" && (
										<Form className={styles.typification}>
											<Text className={styles.textTypificacion}>Texto:</Text>
											<TextArea
												value={note}
												name="messageArea"
												onChange={(e) => {
													return setNote(e.target.value);
												}}
												className={styles.inputGroupB}
												minRows={1}
											/>
										</Form>
									)}
							</FilterCard>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

FilterMaster.propTypes = {
	errors: PropTypes.object,
	options: PropTypes.array,
	selectedFilterTemp: PropTypes.string,
	onChangeSelection: PropTypes.func,
	startDate: PropTypes.string,
	endDate: PropTypes.string,
	handleInput: PropTypes.func,
	getMaxDate: PropTypes.func,
	tempOrderBy: PropTypes.string,
	handleOrderByChange: PropTypes.func,
	getOrderByOptions: PropTypes.func,
	orderDirectionOptions: PropTypes.array,
	tempOrderDirection: PropTypes.string,
	setTempOrderDirection: PropTypes.func,
	contacts: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
	onContactsSelect: PropTypes.func,
	onContactsRemove: PropTypes.func,
	agents: PropTypes.array,
	setAgents: PropTypes.func,
	agentsData: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
	tagsSelected: PropTypes.array,
	handleInputDropdown: PropTypes.func,
	tagsData: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
	note: PropTypes.string,
	setNote: PropTypes.func,
	channelFilters: PropTypes.array,
	channelfiltersData: PropTypes.array,
	groupsData: PropTypes.array,
	groupsfilters: PropTypes.array,
	onChangeDropdown: PropTypes.func,
	cleanModal: PropTypes.func,
	actionsHistory: PropTypes.array,
	setActionsHistory: PropTypes.func,
	categoriesSelected: PropTypes.array,
	categoriesData: PropTypes.array,
	handleCategoriesChange: PropTypes.func,
	stateValue: PropTypes.string,
	setStateValue: PropTypes.func,
	conditionValue: PropTypes.string,
	setConditionValue: PropTypes.func,
	groupBy: PropTypes.string,
	setGroupBy: PropTypes.func,
	section: PropTypes.string,
	setSection: PropTypes.func,
	showTable: PropTypes.bool,
	children: PropTypes.node,
	onApplyFilters: PropTypes.func,
	onEditFilters: PropTypes.func,
	onCancelFilters: PropTypes.func,
	onRemoveActiveFilter: PropTypes.func,
};

export default FilterMaster;
